import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./toast.module.scss";

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  persistent?: boolean;
  actions?: ToastAction[];
}

export interface ToastAction {
  label: string;
  onClick: () => void;
  primary?: boolean;
}

export interface ToastOptions {
  persistent?: boolean;
  actions?: ToastAction[];
}

export interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant, options?: ToastOptions) => number;
  dismissToast: (id: number) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message: string, variant: ToastVariant = "info", options?: ToastOptions) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, message, variant, ...options }]);
    if (!options?.persistent) {
      setTimeout(() => removeToast(id), 5000);
    }
    return id;
  }, [removeToast]);

  // Promote the container to the top layer while any toast is visible.
  // Native <dialog>.showModal() puts modals in the top layer, which beats any
  // z-index; only other top-layer elements stack above it. `popover="manual"` +
  // showPopover() opts the container into the same top layer so toasts remain
  // visible above open modals.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof el.showPopover !== "function") return;
    if (toasts.length > 0 && !el.matches(":popover-open")) {
      try { el.showPopover(); } catch { /* noop */ }
    } else if (toasts.length === 0 && el.matches(":popover-open")) {
      try { el.hidePopover(); } catch { /* noop */ }
    }
  }, [toasts.length]);

  return (
    <ToastContext.Provider value={{ toast: addToast, dismissToast: removeToast }}>
      {children}
      <div ref={containerRef} className={styles.container} popover="manual">
        {toasts.map((t) => (
          <div key={t.id} className={`${styles.toast} ${styles[t.variant]}`} role="alert">
            <div className={styles.content}>
              <span className={styles.message}>{t.message}</span>
              {t.actions && t.actions.length > 0 && (
                <div className={styles.actions}>
                  {t.actions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      className={action.primary ? styles.actionPrimary : styles.actionSecondary}
                      onClick={() => {
                        removeToast(t.id);
                        action.onClick();
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className={styles.closeBtn} onClick={() => removeToast(t.id)} aria-label="Dismiss">&times;</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
